import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export interface AddressData {
  fullName: string;
  street: string;
  city: string;
  state: string;
  pin: string;
}

interface AddressSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (address: AddressData) => void;
  initialAddress: AddressData;
  onBack?: () => void;
}

const AddressSelectionModal: React.FC<AddressSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialAddress,
  onBack,
}) => {
  const [address, setAddress] = useState<AddressData>(initialAddress);

  useEffect(() => {
    if (isOpen) {
      setAddress(initialAddress);
    }
  }, [isOpen, initialAddress]);

  const handleConfirm = () => {
    // Basic validation
    if (!address.fullName || !address.street || !address.city || !address.pin) {
      return;
    }
    onConfirm(address);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="tw-absolute tw-inset-0 tw-bg-black/90 tw-backdrop-blur-xl"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="tw-relative tw-bg-[#0d0d0d] tw-rounded-[32px] tw-w-full tw-max-w-xl tw-overflow-hidden tw-shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] tw-border tw-border-white/5"
          >
            {/* Header */}
            <div className="tw-p-10 tw-flex tw-justify-between tw-items-center">
              <h2 className="tw-text-2xl tw-font-bold tw-text-white">Edit Pickup Address</h2>
              <button
                onClick={onClose}
                className="tw-absolute tw-top-8 tw-right-8 tw-p-2 tw-text-gray-500 hover:tw-text-white tw-bg-transparent tw-border-none tw-cursor-pointer tw-transition-colors"
              >
                <X className="tw-w-6 tw-h-6" />
              </button>
            </div>

            {/* Form */}
            <div className="tw-px-8 tw-pb-8 tw-space-y-6">
              <div className="tw-space-y-6">
                <div>
                  <label className="tw-block tw-text-[10px] tw-font-black tw-text-[#00C5FB] tw-uppercase tw-tracking-widest tw-mb-2">Full Name</label>
                  <input
                    placeholder="Enter full name"
                    value={address.fullName}
                    onChange={(e) => setAddress({ ...address, fullName: e.target.value })}
                    className="tw-w-full tw-bg-[#222225] tw-border tw-border-white/5 tw-rounded-md tw-px-4 tw-py-4 tw-text-white tw-text-sm focus:tw-outline-none focus:tw-border-[#00C5FB]/50 tw-transition-all"
                  />
                </div>

                <div>
                  <label className="tw-block tw-text-[10px] tw-font-black tw-text-[#00C5FB] tw-uppercase tw-tracking-widest tw-mb-2">Street Address</label>
                  <input
                    placeholder="1234 Elm Street, Apt 56"
                    value={address.street}
                    onChange={(e) => setAddress({ ...address, street: e.target.value })}
                    className="tw-w-full tw-bg-[#222225] tw-border tw-border-white/5 tw-rounded-md tw-px-4 tw-py-4 tw-text-white tw-text-sm focus:tw-outline-none focus:tw-border-[#00C5FB]/50 tw-transition-all"
                  />
                </div>

                <div className="tw-grid tw-grid-cols-12 tw-gap-4">
                  <div className="tw-col-span-4">
                    <label className="tw-block tw-text-[10px] tw-font-black tw-text-[#00C5FB] tw-uppercase tw-tracking-widest tw-mb-2">City</label>
                    <input
                      placeholder="City"
                      value={address.city}
                      onChange={(e) => setAddress({ ...address, city: e.target.value })}
                      className="tw-w-full tw-bg-[#222225] tw-border tw-border-white/5 tw-rounded-md tw-px-4 tw-py-4 tw-text-white tw-text-sm focus:tw-outline-none focus:tw-border-[#00C5FB]/50 tw-transition-all"
                    />
                  </div>
                  <div className="tw-col-span-4">
                    <label className="tw-block tw-text-[10px] tw-font-black tw-text-[#00C5FB] tw-uppercase tw-tracking-widest tw-mb-2">State</label>
                    <div className="tw-relative">
                      <input
                        placeholder="State"
                        value={address.state}
                        onChange={(e) => setAddress({ ...address, state: e.target.value })}
                        className="tw-w-full tw-bg-[#222225] tw-border tw-border-white/5 tw-rounded-md tw-px-4 tw-py-4 tw-text-white tw-text-sm focus:tw-outline-none focus:tw-border-[#00C5FB]/50 tw-transition-all"
                      />
                    </div>
                  </div>
                  <div className="tw-col-span-4">
                    <label className="tw-block tw-text-[10px] tw-font-black tw-text-[#00C5FB] tw-uppercase tw-tracking-widest tw-mb-2">Pin Code</label>
                    <input
                      placeholder="400031"
                      value={address.pin}
                      onChange={(e) => setAddress({ ...address, pin: e.target.value })}
                      className="tw-w-full tw-bg-[#222225] tw-border tw-border-white/5 tw-rounded-md tw-px-4 tw-py-4 tw-text-white tw-text-sm focus:tw-outline-none focus:tw-border-[#00C5FB]/50 tw-transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="tw-p-10 tw-flex tw-gap-4 tw-pt-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="tw-px-6 tw-py-4 tw-rounded-xl tw-bg-transparent tw-text-gray-400 tw-font-bold tw-text-sm tw-border tw-border-white/10 tw-cursor-pointer hover:tw-bg-white/5 tw-transition-all"
                >
                  Back
                </button>
              )}
              <button
                onClick={handleConfirm}
                className="tw-flex-1 tw-py-4 tw-rounded-xl tw-bg-[#00C5FB] tw-text-black tw-font-bold tw-text-sm tw-border-none tw-cursor-pointer hover:tw-bg-cyan-400 tw-transition-all tw-shadow-[0_10px_30px_rgba(0,197,251,0.4)]"
              >
                Confirm Address
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddressSelectionModal;
